import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type AlertVariant = 'error' | 'success' | 'warning' | 'info';

/**
 * Clase para los avisos que añaden un icono propio delante del texto.
 *
 * El icono queda **en línea** con el texto (con un margen a su derecha), en lugar de
 * ser un elemento de un contenedor `flex`: dentro de un `flex`, cada trozo de texto y
 * cada `<strong>` son elementos independientes y el párrafo se parte en bloques al
 * pasar de línea.
 */
export const ICONO_EN_ALERTA = '[&>svg]:mr-1.5 [&>svg]:inline [&>svg]:align-text-bottom';

const VARIANTS: Record<AlertVariant, { wrapper: string; icon: ReactNode }> = {
  error: {
    wrapper: 'border-rose-200 bg-rose-50 text-rose-800',
    icon: <AlertCircle className="size-4.5 text-rose-500" />,
  },
  success: {
    wrapper: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    icon: <CheckCircle2 className="size-4.5 text-emerald-500" />,
  },
  warning: {
    wrapper: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: <TriangleAlert className="size-4.5 text-amber-500" />,
  },
  info: {
    wrapper: 'border-brand-200 bg-brand-50 text-brand-900',
    icon: <Info className="size-4.5 text-brand-500" />,
  },
};

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: ReactNode;
  className?: string;
}

export function Alert({ variant = 'info', title, children, className }: AlertProps) {
  const { wrapper, icon } = VARIANTS[variant];

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-control border px-3.5 py-3 text-sm', wrapper, className)}
    >
      <span aria-hidden="true" className="mt-px shrink-0">
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className={cn('leading-relaxed', title && 'mt-0.5 text-[13px] opacity-90')}>{children}</div>
      </div>
    </div>
  );
}
